import React from "react";
import {
  Box,
  InputBase,
  MenuItem,
  Select,
  Typography,
  createStyles,
  withStyles
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { magicLinkColors, magicLinkRadius } from "./magicLinkTokens";

const ERROR_COLOR = "#DC1B09";

const styles = (theme: any) =>
  createStyles({
    field: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
      width: "100%"
    },
    label: {
      fontFamily: "Nunito Sans",
      fontSize: "14px",
      fontWeight: 600,
      lineHeight: "16px",
      color: magicLinkColors.textStrong,
      marginBottom: 4
    },
    selectLabel: {
      fontSize: "12px"
    },
    input: {
      height: 40,
      boxSizing: "border-box",
      padding: "0 12px",
      borderRadius: magicLinkRadius.card,
      border: `1px solid ${magicLinkColors.border}`,
      backgroundColor: magicLinkColors.option,
      fontFamily: "Nunito Sans",
      fontSize: "12px",
      color: magicLinkColors.text,
      transition: "border-color 150ms ease",
      "&:hover": {
        borderColor: theme.palette.primary.main
      },
      "& input": {
        padding: 0,
        height: "100%",
        fontSize: "12px",
        "&::placeholder": {
          color: magicLinkColors.text,
          opacity: 1
        }
      }
    },
    focused: {
      borderColor: theme.palette.primary.main
    },
    error: {
      borderColor: ERROR_COLOR,
      "&:hover": {
        borderColor: ERROR_COLOR
      }
    },
    disabled: {
      backgroundColor: magicLinkColors.border,
      "&:hover": {
        borderColor: magicLinkColors.border
      }
    },
    select: {
      padding: "0 24px 0 0 !important",
      height: "100%",
      display: "flex",
      alignItems: "center",
      fontSize: "12px",
      backgroundColor: "transparent !important"
    },
    selectIcon: {
      fontSize: 18,
      right: 12,
      color: magicLinkColors.textStrong
    },
    menuItem: {
      fontFamily: "Nunito Sans",
      fontSize: "12px",
      padding: "10px 12px"
    },
    helper: {
      fontFamily: "Nunito Sans",
      fontSize: "12px",
      lineHeight: "16px",
      color: ERROR_COLOR,
      marginTop: 2
    },
    adornment: {
      fontSize: "12px",
      color: magicLinkColors.text,
      marginRight: 4
    }
  });

interface BaseProps {
  label: string;
  name: string;
  id?: string;
  error?: string | false;
  disabled?: boolean;
  classes: Record<string, string>;
}

interface TextFieldProps extends BaseProps {
  value: string | number;
  placeholder?: string;
  type?: string;
  prefix?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface SelectFieldProps extends BaseProps {
  value: string | number;
  options: SelectOption[];
  placeholder?: string;
  onChange: (event: React.ChangeEvent<{ name?: string; value: unknown }>) => void;
}

const inputClasses = (classes: Record<string, string>) => ({
  root: classes.input,
  focused: classes.focused,
  error: classes.error,
  disabled: classes.disabled
});

const FieldShell = ({
  label,
  error,
  htmlFor,
  labelClassName,
  classes,
  children
}: {
  label: string;
  error?: string | false;
  htmlFor?: string;
  labelClassName?: string;
  classes: Record<string, string>;
  children: React.ReactNode;
}) => (
  <Box className={classes.field}>
    <Typography
      component="label"
      htmlFor={htmlFor}
      className={[classes.label, labelClassName].filter(Boolean).join(" ")}
    >
      {label}
    </Typography>
    {children}
    {error && <Typography className={classes.helper}>{error}</Typography>}
  </Box>
);

const RawTextField = ({
  label,
  name,
  id,
  value,
  placeholder,
  type,
  prefix,
  error,
  disabled,
  onChange,
  onBlur,
  onKeyPress,
  classes
}: TextFieldProps) => (
  <FieldShell label={label} error={error} htmlFor={id} classes={classes}>
    <InputBase
      id={id}
      name={name}
      type={type}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      error={!!error}
      onChange={onChange}
      onBlur={onBlur}
      onKeyPress={onKeyPress}
      classes={inputClasses(classes)}
      startAdornment={
        prefix ? <span className={classes.adornment}>{prefix}</span> : undefined
      }
    />
  </FieldShell>
);

const RawSelectField = ({
  label,
  name,
  id,
  value,
  options,
  placeholder = "Select",
  error,
  disabled,
  onChange,
  classes
}: SelectFieldProps) => (
  <FieldShell
    label={label}
    error={error}
    htmlFor={id}
    labelClassName={classes.selectLabel}
    classes={classes}
  >
    <Select
      id={id}
      name={name}
      value={value ?? ""}
      displayEmpty
      disabled={disabled}
      onChange={onChange}
      IconComponent={ExpandMoreIcon}
      input={<InputBase error={!!error} classes={inputClasses(classes)} />}
      classes={{ select: classes.select, icon: classes.selectIcon }}
      renderValue={(selected) => {
        const option = options.find((item) => item.value === selected);
        return option ? option.label : placeholder;
      }}
    >
      {options.map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          className={classes.menuItem}
        >
          {option.label}
        </MenuItem>
      ))}
    </Select>
  </FieldShell>
);

export const MagicLinkTextField = withStyles(styles)(RawTextField);
export const MagicLinkSelectField = withStyles(styles)(RawSelectField);
